// libraries
import React, { useState, useRef, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// svg
import { GeneralWarning } from '/react4xp/components/Svg/Icons/General'

interface SearchProps {
    id: string
    name: string
    label?: string
    type: string
    placeholder?: string
    required?: boolean
    maxLength?: number
    minLength?: number
    value?: string
    disabled?: boolean
    size?: 'small' | 'medium' | 'large' | 'full-width'
    isWhite?: boolean
    showHidePassword?: boolean
    className?: string
    errorMessages?: {
        required?: string
        maxLength?: string
        minLength?: string
        invalidEmail?: string
        invalidSuggestion?: string
        alreadySelected?: string
        alreadySelectedUrlText?: string
        alreadySelectedUrl?: string
    }
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    endpoint: string
    onSuggestionSelect?: (suggestion: { id: string, name: string }) => void
    isValidVolunteerRegisterEndpoint?: string
}

export default function Search({
    id,
    name,
    label,
    type,
    placeholder,
    required,
    maxLength,
    minLength,
    value,
    disabled,
    size = 'full-width',
    isWhite,
    className,
    errorMessages,
    onChange = () => {},
    onKeyDown,
    endpoint,
    onSuggestionSelect,
    isValidVolunteerRegisterEndpoint
}: SearchProps) {

    const formContext = useFormContext()
    const register = formContext ? formContext.register : () => ({})
    const errors = formContext ? formContext.formState.errors : {}
    const trigger = formContext ? formContext.trigger : () => Promise.resolve(true)

    const [suggestions, setSuggestions] = useState<Array<{
        id: string
        name: string
    }>>([])
    const [inputValue, setInputValue] = useState(value || '')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [isValidSelection, setIsValidSelection] = useState(false)
    const [nextPage, setNextPage] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const suggestionsRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const [alreadySelected, setAlreadySelected] = useState(false)
    const [alreadySelectedUrl, setAlreadySelectedUrl] = useState(errorMessages?.alreadySelectedUrl || null)

    let validations: RegisterOptions = {
        required,
        validate: {
            isValidSelection: () => {
                if (alreadySelected) {
                    return errorMessages?.invalidSuggestion
                }
                return isValidSelection || errorMessages?.invalidSuggestion
            }
        }
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    if (maxLength) {
        validations.maxLength = {
            value: maxLength,
            message: errorMessages?.maxLength
        }
    }

    if (minLength) {
        validations.minLength = {
            value: minLength,
            message: errorMessages?.minLength
        }
    }

    const errorMessage = errors && errors[name]?.message

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && onKeyDown) {
            onKeyDown(event)
        }
    }

    const fetchSuggestions = async (searchValue: string, isLoadMore: boolean = false) => {
        if (isLoading) return
        setIsLoading(true)

        try {
            const url = new URL(isLoadMore ? nextPage! : endpoint)
            if (!isLoadMore) {
                const queryParams = new URLSearchParams()
                queryParams.append('navn', searchValue)
                queryParams.append('konkurs', 'false')
                queryParams.append('institusjonellSektorkode', '7000')
                queryParams.append('registrertIFrivillighetsregisteret', 'true')
                url.search = queryParams.toString()
            }

            const response = await fetch(url)
            if (!response.ok) throw new Error('Network response was not ok')
            
            const data = await response.json()
            const registerList = (data?._embedded?.enheter || []).map((register) => ({
                id: register.organisasjonsnummer,
                name: register.navn
            }))

            //console.log(registerList)

            setSuggestions(prev => isLoadMore ? [...prev, ...registerList] : registerList)
            setNextPage(data?._links?.next?.href || null)
            setShowSuggestions(true)
        } catch (error) {
            console.error('Error fetching suggestions:', error)
            setSuggestions([])
        } finally {
            setIsLoading(false)
        }
    }

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)
        setIsValidSelection(false)
        setAlreadySelected(false)
        if (onSuggestionSelect) {
            onSuggestionSelect(null)
        }
        onChange(e)

        if (value.length > 2) {
            setNextPage(null)
            await fetchSuggestions(value)
        } else {
            setSuggestions([])
            setShowSuggestions(false)
        }
    }

    const handleSuggestionClick = async (suggestion: { id: string, name: string }) => {
        setIsValidSelection(true)
        if (isValidVolunteerRegisterEndpoint && suggestion.id) {
            setAlreadySelected(false)
            fetch(`${isValidVolunteerRegisterEndpoint}&id=${suggestion.id}`).then(response => {
                if (!response.ok) return { isValid: false, url: null }
                return response.json()
            }).then(data => {
                if (!data.isValid) {
                    setAlreadySelected(true)
                    setIsValidSelection(false)
                    setAlreadySelectedUrl(data.url)
                }
            })
        }
        setInputValue(suggestion.name)
        setShowSuggestions(false)
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion)
        }

        // uncomment the following lines to test the error message
        //setAlreadySelected(true)
        //setIsValidSelection(false)

        // trigger form validation after selection
        if (formContext) {
            setTimeout(async () => {
                await trigger(name)
            }, 50)
        }
    }

    // load more suggestions when scrolling all the way down
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        if (scrollHeight - scrollTop - clientHeight < 50 && nextPage && !isLoading) {
            fetchSuggestions(inputValue, true)
        }
    }, [nextPage, isLoading, inputValue])

    // close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false)
            }
        }

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showSuggestions])

    // fetch suggestions when the input is focused (if there are more than 2 characters)
    const handleInputFocus = () => {
        if (inputValue.length > 2) {
            fetchSuggestions(inputValue)
        }
    }

    // register the input with react hook form
    const registerResult = register ? register(name || '', validations) : { onChange: undefined }
    const { onChange: registerOnChange, ...registerRest } = registerResult as { onChange?: (e: any) => void }

    return (
        <div 
            className={clsx(
                'form-line',
                className,
                size === 'small' && 'form-line--small',
                size === 'medium' && 'form-line--medium',
                size === 'large' && 'form-line--large',
                size === 'full-width' && 'form-line--full-width',
                errors && errors[name] && 'form-line--error',
                isWhite && 'white'
            )}
            ref={wrapperRef}
        >

            <label className={clsx('form-line__label text-small')} htmlFor={id}>
                {label}
            </label>

            <div className={clsx(
                'form-line__line-wrapper',
                showSuggestions && suggestions.length > 0 && 'form-line__line-wrapper--has-suggestions'
            )}>

                <input
                    type={type}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    className='form-line__input text-small'
                    value={inputValue}
                    disabled={disabled || false}
                    onKeyDown={handleKeyPress}
                    onFocus={handleInputFocus}
                    onChange={(e) => {
                        handleInputChange(e)
                        if (registerOnChange) {
                            registerOnChange(e)
                        }
                    }}
                    {...registerRest}
                />

                {showSuggestions && suggestions.length > 0 && (
                    <div 
                        className='form-line__search-suggestions'
                        onScroll={handleScroll}
                        ref={suggestionsRef}
                    >

                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion.id}
                                type='button'
                                className='search-suggestion-item'
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                {suggestion.name}
                            </button>
                        ))}

                        {isLoading && (
                            <div className='search-suggestion-loading'>
                                Loading more...
                            </div>
                        )}

                    </div>
                )}

            </div>

            {alreadySelected && (
                <p className='form-line__already-selected text-smaller'>
                    {errorMessages?.alreadySelected} <a href={alreadySelectedUrl || '#'} target='_blank' className='link-w-icon'>{errorMessages?.alreadySelectedUrlText}</a>
                </p>
            )}

            {errorMessage && (
                <p className={clsx('form-line__error-message', 'text-smaller')}>
                    <GeneralWarning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}